/**
 * @file Автоматический вход по одноразовой ссылке.
 *
 * Оформление общее с обычным входом. Вызов API и защита одноразовой операции
 * вынесены в session/oneTimeSignIn.ts; смена языка не запускает вход заново.
 */
import { useEffect, useState } from 'react'
import { FiAlertCircle, FiLink } from 'react-icons/fi'
import { Link, useNavigate } from 'react-router-dom'

import { useCustomer } from '../context/useCustomer'
import AuthCard from '../features/session/AuthCard'
import { oneTimeMessages } from '../features/session/oneTimeMessages'
import { oneTimeSignInErrorCode } from '../features/session/oneTimeSignIn'
import type {
    OneTimeSignInAttempt,
    OneTimeSignInErrorCode,
} from '../features/session/oneTimeSignIn'
import { useLocale } from '../i18n/useLocale'

export default function SignInOTAT({
    attempt,
}: {
    attempt: OneTimeSignInAttempt | null
}) {
    const { setCustomer, setPassword } = useCustomer()
    const { locale } = useLocale()
    const text = oneTimeMessages[locale]
    const navigate = useNavigate()
    // attempt === null — при загрузке приложения одноразовую ссылку не захватили.
    // Это возможно при переходе на /otat внутри уже открытого UI.
    // error === null означает ожидание результата, а не подтверждённый вход.
    const [error, setError] = useState<OneTimeSignInErrorCode | null>(
        attempt ? null : 'invalidLink',
    )

    // Смена языка и StrictMode не должны повторять одноразовый обмен.
    useEffect(() => {
        // Ошибка invalidLink уже выбрана выше; запускать запрос без попытки нечего.
        if (!attempt) return

        const lease = attempt.acquire()

        // active относится только к этому запуску эффекта. После cleanup его
        // обработчики теряют право менять экран, даже если общий запрос ещё идёт.
        let active = true

        lease.promise
            .then((customer) => {
                if (!active) return

                // В Promise уже получена и проверена сессия. Сначала публикуем
                // пользователя, затем заменяем URL, чтобы Back не повторял OTAT.
                // Новый аккаунт не должен наследовать пароль шифрования предыдущего.
                setPassword(null)
                setCustomer(customer)
                navigate('/dashboard', { replace: true })
            })
            .catch((cause) => {
                if (active) setError(oneTimeSignInErrorCode(cause))
            })

        // Важно выполнить обе части: active запрещает позднее изменение UI,
        // release сообщает общей попытке, что этот эффект больше не ждёт ответ.
        return () => {
            active = false
            lease.release()
        }
    }, [attempt, setCustomer, setPassword, navigate])

    // Пока нет ошибки, показываем ожидание. Успех переводит на dashboard,
    // поэтому отдельного постоянного состояния «успешно» у этой страницы нет.
    return (
        <AuthCard title={text.title} description={text.description}>
            {/* Ожидание и ошибка — два представления одной попытки; успешный вход меняет маршрут. */}
            <div className={`one-time-state${error ? ' one-time-failed' : ''}`}>
                <span className='one-time-icon' aria-hidden='true'>
                    {error ? <FiAlertCircle /> : <FiLink />}
                </span>
                {error ? (
                    <div role='alert'>
                        <h2>{text.errorTitle}</h2>

                        <p className='sign-in-error'>{text.errors[error]}</p>
                    </div>
                ) : (
                    <div role='status'>
                        <h2>{text.pending}</h2>

                        <p>{text.pendingHint}</p>

                        <span
                            className='one-time-progress'
                            aria-hidden='true'
                        />
                    </div>
                )}
            </div>

            {/* Повторного расходования токена нет: после ошибки предлагаем обычный вход или новую ссылку. */}
            {error && (
                <>
                    <p className='one-time-hint'>{text.newLink}</p>

                    <Link className='one-time-back' to='/' replace>
                        {text.back}
                    </Link>
                </>
            )}
        </AuthCard>
    )
}
